import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar, GripVertical } from 'lucide-react';
import { isBefore, parseISO, format } from 'date-fns';
import { TICKET_CATEGORIES, PRIORITIES } from '../../services/whatsapp/waConstants';

export interface KanbanTicket {
  id: string;
  title: string;
  client: string;
  category: string;
  priority: string;
  assignee: string;
  assigneeAvatar: string;
  dueDate: string | null;
  columnId: string;
  estimatedHours?: number;
  description?: string;
  conversationId?: string;
  aiSummary?: string;
  onClick?: () => void;
}

interface KanbanCardProps {
  ticket: KanbanTicket;
  isOverlay?: boolean;
}

export default function KanbanCard({ ticket, isOverlay }: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ticket.id,
    disabled: isOverlay,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Quando arrastando: esconder o original, o DragOverlay renderiza a cópia visível
    opacity: isDragging ? 0 : 1,
  };

  const category = TICKET_CATEGORIES[ticket.category] || TICKET_CATEGORIES.outros;
  const priority = PRIORITIES[ticket.priority] || PRIORITIES.medium;

  const isOverdue = ticket.dueDate ? isBefore(parseISO(ticket.dueDate), new Date()) : false;
  const formattedDate = ticket.dueDate ? format(parseISO(ticket.dueDate), 'dd/MM') : null;

  const priorityDotColors: Record<string, string> = {
    low: 'bg-[#8E8E93]',
    medium: 'bg-[#007AFF]',
    high: 'bg-[#FF9500]',
    urgent: 'bg-[#FF2D55]',
  };

  return (
    <div
      ref={isOverlay ? undefined : setNodeRef}
      style={isOverlay ? undefined : style}
      className={`group cursor-pointer rounded-xl border border-gray-100 bg-white p-3.5 shadow-sm transition-all hover:shadow-md ${
        isOverlay ? 'rotate-2 scale-105 shadow-lg' : ''
      }`}
      role="button"
      tabIndex={0}
      onClick={ticket.onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') ticket.onClick?.();
      }}
    >
      <div className="mb-2 flex items-start justify-between">
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${category.bg} ${category.color}`}
        >
          {category.label}
        </span>
        {!isOverlay && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab touch-none rounded-lg p-0.5 opacity-0 transition-all hover:bg-gray-100 active:cursor-grabbing group-hover:opacity-100"
          >
            <GripVertical size={14} className="text-gray-400" />
          </button>
        )}
      </div>

      <h4 className="mb-1 text-sm font-semibold leading-snug text-gray-900">{ticket.title}</h4>
      <p className="mb-3 text-xs text-gray-500">{ticket.client}</p>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${priorityDotColors[ticket.priority] || 'bg-[#8E8E93]'}`}
            title={priority.label}
          />
          <div
            className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-green-600 text-[10px] font-bold text-white shadow-sm"
            title={ticket.assignee}
          >
            {ticket.assigneeAvatar}
          </div>
        </div>
        {formattedDate && (
          <div
            className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${isOverdue ? 'bg-[#FF2D55]/10 font-semibold text-[#FF2D55]' : 'text-gray-400'}`}
          >
            <Calendar size={11} />
            {formattedDate}
          </div>
        )}
      </div>
    </div>
  );
}
